import { useState } from 'react';
import { beginSessionFlow } from '../../stores/session-read';
import { motion } from 'framer-motion';
import { FiMail, FiRefreshCw, FiLogOut } from 'react-icons/fi';
import { useAuthStore } from '../../stores/auth.store';
import { resendVerificationEmail, changeUnverifiedEmail } from '../../services/profile.service';

/**
 * Blocking screen shown to a new (post-cutoff) user whose email isn't verified yet.
 * This is UX only — the real enforcement is the server `verified-new` middleware — but
 * it tells the user what to do instead of letting them hit silent 403s.
 *
 * Offers: resend, "Continue" (re-checks /me after they click the email link, which opens
 * the web verify page on any client), and change-email for a typo'd address.
 */
export default function VerifyEmailGate() {
  const user = useAuthStore((s) => s.user);
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const logout = useAuthStore((s) => s.logout);

  const [busy, setBusy] = useState<null | 'resend' | 'continue' | 'change'>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  async function handleResend() {
    setBusy('resend');
    setNotice(null);
    setError(null);
    const res = await resendVerificationEmail();
    if (res.success) setNotice(res.message || 'Verification email sent — check your inbox.');
    else setError(res.error || 'Could not send the email. Try again shortly.');
    setBusy(null);
  }

  async function handleContinue() {
    setBusy('continue');
    setError(null);
    setNotice(null);
    const checked = await refreshUser();
    // null: the session that asked has ended (#940). Nothing to tell anyone.
    if (checked === null) {
      setBusy(null);
      return;
    }

    // Branch on what actually happened. This used to set the notice
    // unconditionally, so it cheerfully said "you're all set" while the wall was
    // still up — a green success message on a screen that had just refused to let
    // you past. If verification DID land, this component unmounts and no message
    // is needed at all.
    const verified = useAuthStore.getState().user?.email_verified === true;

    if (!checked) {
      // We never reached the server, so we do not know. Saying "still not
      // verified" here blames the person for a network failure, and sends them
      // hunting through an inbox that may already have the answer.
      setError('Could not check just now — you may be offline. Try again in a moment.');
    } else if (!verified) {
      setError(
        'Still not verified. Open the link in your inbox, then try again — or resend below.',
      );
    }

    setBusy(null);
  }

  async function handleChange(e: React.FormEvent) {
    e.preventDefault();
    // Captured before the first await; every step runs under it (#940 r7).
    const flow = beginSessionFlow();
    setBusy('change');
    setNotice(null);
    setError(null);
    const changed = await flow.step(() => changeUnverifiedEmail(newEmail.trim()));
    if (changed.status === 'ended') {
      setBusy(null);
      return;
    }
    const res =
      changed.status === 'ok'
        ? changed.value
        : { success: false as const, error: undefined, message: undefined };
    if (res.success) {
      setNotice(res.message || 'Email updated — check your new inbox.');
      setEditing(false);
      setNewEmail('');
      await flow.step(() => refreshUser());
    } else {
      setError(res.error || 'Could not update your email.');
    }
    setBusy(null);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-(--ee-tw-orange-50-bg) to-(--ee-tw-amber-50-bg) flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-(--ee-surface) rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
          <FiMail className="w-10 h-10 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-(--ee-tw-gray-900) mb-2">Verify your email</h1>
        <p className="text-(--ee-tw-gray-600) mb-1">We sent a verification link to</p>
        <p className="font-medium text-(--ee-tw-gray-900) mb-6 break-all">{user?.email}</p>

        {notice ? (
          <div className="alert alert-success text-sm mb-4 text-left" role="status">
            {notice}
          </div>
        ) : null}
        {error ? (
          <div className="alert alert-error text-sm mb-4 text-left" role="alert">
            {error}
          </div>
        ) : null}

        <button
          onClick={() => void handleContinue()}
          disabled={busy !== null}
          className="w-full py-3 px-4 bg-primary hover:bg-primary-focus text-(--ee-on-accent) font-medium rounded-xl transition-colors disabled:opacity-60 mb-3"
        >
          {busy === 'continue' ? 'Checking…' : "I've verified — continue"}
        </button>

        <button
          onClick={() => void handleResend()}
          disabled={busy !== null}
          className="w-full py-3 px-4 border border-(--ee-tw-gray-300-line) hover:bg-(--ee-tw-gray-50-bg) text-(--ee-tw-gray-700) font-medium rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          <FiRefreshCw className={busy === 'resend' ? 'animate-spin' : ''} />
          Resend email
        </button>

        {editing ? (
          <form onSubmit={(e) => void handleChange(e)} className="mt-4 text-left">
            <label htmlFor="new-email" className="text-sm text-(--ee-tw-gray-600)">
              <span className="block mb-1">New email address</span>
              <input
                id="new-email"
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="input input-bordered w-full"
                placeholder="you@example.com"
              />
            </label>
            <div className="flex gap-2 mt-2">
              <button
                type="submit"
                disabled={busy !== null}
                className="btn btn-primary btn-sm flex-1"
              >
                {busy === 'change' ? 'Saving…' : 'Update & resend'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="btn btn-ghost btn-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="mt-4 text-sm text-primary hover:underline"
          >
            Wrong email? Change it
          </button>
        )}

        <div className="mt-8 pt-6 border-t border-(--ee-tw-gray-200-line)">
          <button
            onClick={() => void logout()}
            className="text-xs text-(--ee-tw-gray-500) hover:text-(--ee-tw-gray-700) inline-flex items-center gap-1"
          >
            <FiLogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </motion.div>
    </div>
  );
}
